import { FC, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { getPublicationState } from '@/graphql/api';
import { useGetArticleCardsQuery } from '@/graphql/generated';
import { useLocale } from '@/hooks';

import { AllArticles } from './AllArticles';

const ARTICLES_DEFAULT_PAGE = 1;

// Don't forget to add "propsBuilder.withQuery(useGetArticleCardsQuery, {...});"
// and "propsBuilder.withQuery(useGetArticleCategoriesQuery);" into "getStaticProps" on target page.
// Without it data fetching will work only on the browser side.

type AllArticlesSectionProps = {
  isPreview: boolean;
  articlesPerPage: number;
};
export const AllArticlesSection: FC<AllArticlesSectionProps> = ({ isPreview, articlesPerPage }) => {
  // selectedCategoryId is "undefined" because we use value for graphql request, "null" or "empty string" will affect request logic
  const router = useRouter();
  const locale = useLocale();

  const [selectedArticlesPage, setSelectedArticlesPage] = useState<number>(ARTICLES_DEFAULT_PAGE);
  const [searchValue, setSearchValue] = useState('');
  const queryCategoryId = router.query.selectedCategoryId as string;

  useEffect(() => {
    const { query } = router;
    if (query.page) {
      setSelectedArticlesPage(Number(query.page));
    }
  }, [router, router.query]);

  const publicationState = getPublicationState(isPreview);
  const articlesQuery = useGetArticleCardsQuery({
    publicationState,
    categoryId: queryCategoryId ?? undefined,
    page: selectedArticlesPage,
    pageSize: articlesPerPage,
    sort: ['creationDate:desc'],
    title: searchValue,
    locale,
  });
  const articlesData = articlesQuery?.data?.articles?.data;
  const articlesMeta = articlesQuery?.data?.articles?.meta;

  const allArticles = useMemo(() => articlesData?.map((a) => a.attributes!) || [], [articlesData]);

  const handleFilterByCategory = (categoryId?: string) => {
    if (categoryId) {
      router.replace({
        query: { selectedCategoryId: categoryId },
      });
    } else {
      //reset query params to have undefined on empty category
      router.replace({ query: {} });
    }
    setSelectedArticlesPage(ARTICLES_DEFAULT_PAGE);
  };

  const handleChangeArticlesPage = (page: number) => setSelectedArticlesPage(page);

  return (
    <AllArticles
      isLoading={articlesQuery.isLoading}
      // this fix is needed because of a bug in CMS - if category is empty, it returns pageCount as if requested all articles
      pageCount={!!articlesData?.length ? articlesMeta?.pagination.pageCount || ARTICLES_DEFAULT_PAGE : 0}
      articles={allArticles}
      selectedCategoryId={queryCategoryId || undefined}
      selectedPage={selectedArticlesPage}
      onSelectCategory={handleFilterByCategory}
      onPageChange={handleChangeArticlesPage}
      setSearchValue={setSearchValue}
    />
  );
};
